import {SlidersHorizontal} from "lucide-react";
import {Checkbox} from "@sakura-grp/ui/checkbox";
import {Label} from "@sakura-grp/ui/label";
import {useAssignedStores} from "@/hooks/useAssignedStores";
import {useImeSafeSearch} from "@/hooks/useImeSafeSearch";
import {useUIStore} from "@/stores/ui";
import {useCallback, useEffect, useState} from "react";
import {FilterDialog} from "./filter-dialog";

export const CustomerListToolbar = () => {
  const { data: assignedStores = [] } = useAssignedStores();
  const { filters, setFilters, toggleStoreFilter, initializeStoreFilters } = useUIStore();
  const selectedStoreIds = filters.selectedStoreIds;
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const handleApplySearch = useCallback((value: string) => {
    setFilters({search: value});
  }, [setFilters]);

  const search = useImeSafeSearch({
    initialValue: filters.search,
    onApply: handleApplySearch,
  });

  // フィルターが有効かどうか
  const isFilterActive = filters.dateRange !== null
    || !(filters.statusTypes.includes('good') && filters.statusTypes.includes('poor'));

  // 所属店舗が読み込まれたら、フィルターを初期化（全店舗選択状態）
  useEffect(() => {
    if (assignedStores.length > 0) {
      const storeIds = assignedStores.map(s => s.id);
      initializeStoreFilters(storeIds);
    }
  }, [assignedStores, initializeStoreFilters]);

  return (
    <div className="border-t bg-white">
      <div className="px-lg gap-md h-(--h-list) flex items-center border-b">
        {/* 検索ボックス（独立） */}
        <input
          data-slot="input"
          className="h-xl bg-input/10 text-md shadow-xs selection:bg-primary selection:text-primary-foreground file:text-foreground placeholder:text-muted-foreground/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex grow min-w-[4rem] shrink rounded-lg border px-3 py-1 outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          placeholder="名前・カナ・会社名で検索"
          {...search.inputProps}
        />
        {/* 絞り込みボタン（絞る + 登録日で絞る を統合） */}
        <button
          data-slot="popover-trigger"
          className={`h-xl p-md shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 inline-flex items-center justify-start whitespace-nowrap rounded-md border text-left text-xs font-normal tracking-tight outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 ${isFilterActive ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-background'}`}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={filterDialogOpen}
          onClick={() => setFilterDialogOpen(true)}
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />
          絞込
        </button>
        <FilterDialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen} />
        {assignedStores.length > 0 && (
          <div
            className="!divide-(--border) grid flex-none grow items-center divide-x rounded-lg border bg-white text-xs font-bold"
            style={{ gridTemplateColumns: `repeat(${assignedStores.length}, minmax(0, 1fr))` }}
          >
            {assignedStores.map((store) => (
              <div key={store.id} className="gap-xs flex items-center justify-center">
                <Checkbox
                  id={`store-${store.id}`}
                  checked={selectedStoreIds.includes(store.id)}
                  onCheckedChange={() => toggleStoreFilter(store.id)}
                  className="size-(--bigger-sm)"
                />
                <Label
                  htmlFor={`store-${store.id}`}
                  className="h-xl gap-md flex cursor-pointer select-none items-center text-sm font-bold leading-none tracking-tight"
                >
                  {store.short_name}
                </Label>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
